//#region IMPORTS
import { useAppDispatch, useAppSelector } from "../../store/configureStore";
import { Fragment, useEffect, useState } from "react";
import { fetchPromosAsync } from "../promos/promosSlice";
import { clearBackOrder } from "../backorder/backOrderSlice";
import { useTranslation } from "react-i18next";
import { clearBasket } from "./basketSlice";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import BasketPageItem from "./BasketPageItem";
import LoadingSpinner from "../../components/loading/LoadingSpinner";
import ArrowIconRight from "../../components/icons/ArrowIconRight";
import GoBackButton from "../../components/genericButtons/goBackButton/GoBackButton";
import BasketEmpty from "../../components/basket/BasketEmpty";
import PageHeader from "../../components/genericPageHeader/PageHeader";
import NotFound from "../../components/errors/NotFound";
import agent from "../../api/agent";
//#endregion

const ctaBase =
  "min-w-[10rem] py-4 px-4 mb-4 font-medium border border-black bg-white text-black";

export default function BasketPage() {
  //#region SETUP
  // Translation
  const { t } = useTranslation();

  // Router
  const history = useHistory();

  // Redux
  const dispatch = useAppDispatch();
  const { basket } = useAppSelector((state) => state.basket);
  const { promosLoaded } = useAppSelector((state) => state.promos);

  // Local state
  const [loading, setLoading] = useState(false);
  const [deleteWarning, setDeleteWarning] = useState(false);
  //#endregion

  //#region LOGIC
  const itemCount = basket?.items.reduce((sum, item) => sum + item.quantity, 0);

  // Load promos
  useEffect(() => {
    const fetchOnload = async () => {
      !promosLoaded && (await dispatch(fetchPromosAsync()));
    };
    fetchOnload();
  }, [promosLoaded, dispatch]);

  const handleCheckout = async () => {
    setLoading(true);
    try {
      history.push("/checkout");
    } catch (error) {
      toast.error(`${error}`);
    }
  };

  const handleDeleteBasket = async () => {
    if (!basket) return NotFound();
    setLoading(true);
    try {
      await agent.Basket.delete(basket!.id);
      dispatch(clearBasket());
      dispatch(clearBackOrder());
      setLoading(false);
      setDeleteWarning(false);
      toast.success(t("rituals.yourBasketIsEmpty"));
      history.push("/");
    } catch (error) {
      toast.error(`${error}`);
    }
  };

  const handleReturnToPromo = () => {
    if (basket?.promo_id !== null) {
      history.push(`/backorder/promo/${basket?.promo_id}`);
    }
  };
  //#endregion

  //#region FAIL SAFE
  if (!basket) return <BasketEmpty />;
  if (loading) return <LoadingSpinner />;
  //#endregion

  //#region RENDER
  return (
    <main>
      <PageHeader
        title={t("rituals.myBasket")}
        extra={
          <Fragment>
            <div className="text-right">
              <div>
                <p>
                  <b>{t("rituals.summary")}</b>
                </p>
                <p>
                  {t("rituals.totalQuantity")}: {itemCount}
                </p>
              </div>
            </div>
          </Fragment>
        }
      >
        <GoBackButton />
      </PageHeader>

      <section className="w-full mb-4">
        {basket.items.map((item) => (
          <BasketPageItem key={item.product_version_id} item={item} />
        ))}
      </section>

      <footer className="w-full md:flex md:items-center md:justify-between">
        <div className="flex justify-between">
          {!deleteWarning ? (
            <Fragment>
              <button onClick={handleReturnToPromo} className={ctaBase}>
                {t("rituals.addMore")}
              </button>
              <button
                onClick={() => setDeleteWarning(true)}
                className={`${ctaBase} hover:bg-[#f25c5c] hover:text-black`}
              >
                {t("rituals.deleteBasket")}
              </button>
            </Fragment>
          ) : (
            <div className="flex justify-center items-center mb-4 bg-white">
              <h3 className="text-2xl mr-4">{t("rituals.areYouSure")}</h3>
              <div>
                <button
                  onClick={handleDeleteBasket}
                  className="min-w-[10rem] py-4 px-4 border border-black bg-black text-white font-bold"
                >
                  {t("rituals.yes")}
                </button>
                <button
                  onClick={() => setDeleteWarning(false)}
                  className="min-w-[10rem] py-4 px-4 border border-black bg-white text-[#f25c5c] font-bold"
                >
                  {t("rituals.cancel")}
                </button>
              </div>
            </div>
          )}
        </div>

        <button
          onClick={handleCheckout}
          className="w-full md:w-auto flex items-center justify-center gap-4 min-w-[10rem] py-4 px-4 mb-4 font-semibold border border-black bg-[#3e5902] text-white lg:text-lg"
        >
          <span>{t("rituals.nextStep")}</span>
          <ArrowIconRight color="white" />
        </button>
      </footer>
    </main>
  );
  //#endregion
}
